import React, { createContext, useEffect, useMemo, useState } from "react";
import { getRuntimeConfig } from "./config.js";

/**
 * Stand-ins for the platform contexts and APIs the New Relic shell provides.
 * Shapes match what the SDK exposes so consumers need no changes.
 */
export const PlatformStateContext = createContext({
  accountId: null,
  accountIds: [],
  timeRange: { begin_time: null, end_time: null, duration: 30 * 60 * 1000 },
  timeZone: "UTC",
});

export const NerdletStateContext = createContext({});

const NerdletStateSetterContext = createContext(() => {});

export function PlatformStateProvider({ children, value }) {
  const config = getRuntimeConfig();

  const platformState = useMemo(
    () => ({
      accountId: value?.accountId ?? config.accountId ?? null,
      accountIds: value?.accountId
        ? [value.accountId]
        : config.accountId
        ? [config.accountId]
        : [],
      timeRange: value?.timeRange ?? config.timeRange,
      timeZone:
        value?.timeZone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone ??
        "UTC",
    }),
    [value, config.accountId, config.timeRange]
  );

  const [nerdletState, setNerdletState] = useState(() => readUrlState());

  // Keep the context in step with the address bar, so Back and Forward move the
  // app rather than only the URL.
  useEffect(() => {
    const sync = () =>
      setNerdletState((prev) => {
        const next = readUrlState();
        const same =
          Object.keys(next).length === Object.keys(prev).length &&
          Object.keys(next).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    window.addEventListener("popstate", sync);
    window.addEventListener(URL_STATE_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(URL_STATE_EVENT, sync);
    };
  }, []);

  const mergeNerdletState = useMemo(
    () => (patch) => setNerdletState((prev) => ({ ...prev, ...patch })),
    []
  );

  return (
    <PlatformStateContext.Provider value={platformState}>
      <NerdletStateSetterContext.Provider value={mergeNerdletState}>
        <NerdletStateContext.Provider value={nerdletState}>
          {children}
        </NerdletStateContext.Provider>
      </NerdletStateSetterContext.Provider>
    </PlatformStateContext.Provider>
  );
}

/* ------------------------------------------------------------- url state */

/**
 * The SDK persists nerdlet state in the platform URL. Standalone, the query
 * string is the closest equivalent, so setUrlState round-trips through it.
 */
/** Fired on our own pushState; the browser only emits popstate for back/forward. */
export const URL_STATE_EVENT = "nr1c:urlstate";

function readUrlState() {
  if (typeof window === "undefined") return {};
  try {
    return Object.fromEntries(new URLSearchParams(window.location.search));
  } catch {
    return {};
  }
}

export const nerdlet = {
  setUrlState(patch, options) {
    if (typeof window === "undefined" || !patch) return;
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === undefined || value === "") {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next === current) return;

    // push, not replace: inside New Relic the platform gives each nerdlet state
    // its own history entry, so the browser Back button steps through the views.
    // Replacing left a single entry, and Back exited the app entirely.
    //
    // Restoring a remembered view on a cold load is the exception — the reader
    // did not navigate anywhere, so pushing would put a URL they never saw
    // behind them and Back would look broken. The platform's own setUrlState
    // takes no such option and will simply ignore it.
    if (options && options.replace) {
      window.history.replaceState(null, "", next);
    } else {
      window.history.pushState(null, "", next);
    }
    // pushState fires no event, so tell the provider itself.
    window.dispatchEvent(new Event(URL_STATE_EVENT));
  },

  setConfig: () => {},
  getUrlState: readUrlState,
};

/* ------------------------------------------------------------ navigation */

const ONE_NR_BASE = {
  US: "https://one.newrelic.com",
  EU: "https://one.eu.newrelic.com",
};

function entityUrl(guid) {
  const { region } = getRuntimeConfig();
  const base = ONE_NR_BASE[region] || ONE_NR_BASE.US;
  return `${base}/redirect/entity/${encodeURIComponent(guid)}`;
}

/** Set by the shell so the compat layer can hand entities to the panel. */
let entityOpener = null;

export function setEntityOpener(fn) {
  entityOpener = typeof fn === "function" ? fn : null;
}

/**
 * Inside the platform these open a stacked entity view. That view cannot be
 * embedded standalone — one.newrelic.com sends `X-Frame-Options: DENY` — so the
 * shell registers an opener that renders the entity natively from NerdGraph.
 * Falling back to a new tab keeps the action meaningful if none is registered.
 */
export const navigation = {
  openStackedEntity(guid, options) {
    if (!guid || typeof window === "undefined") return;
    if (entityOpener) {
      entityOpener(guid, options);
      return;
    }
    window.open(entityUrl(guid), "_blank", "noopener,noreferrer");
  },
  openEntity(entity) {
    navigation.openStackedEntity(entity?.guid ?? entity);
  },
  openStackedNerdlet: () => {},
  openNerdlet: () => {},
  getOpenStackedNerdletLocation: () => null,
  getOpenState: readUrlState,
};

export const platform = {
  get accountId() {
    return getRuntimeConfig().accountId;
  },
  get timeRange() {
    return getRuntimeConfig().timeRange;
  },
};

/* ----------------------------------------------------------------- hooks */

export function usePlatformState() {
  return [React.useContext(PlatformStateContext), () => {}];
}

export function useNerdletState() {
  return [
    React.useContext(NerdletStateContext),
    React.useContext(NerdletStateSetterContext),
  ];
}

/* --------------------------------------------------- identity queries */

/**
 * The SDK resolves the signed-in New Relic user. Standalone there is no such
 * user, so this reports whoever the deployment says it is (config.json
 * `userEmail`), resolving immediately.
 */
export function UserQuery({ children }) {
  const { userEmail, userName } = getRuntimeConfig();
  const result = useMemo(
    () => ({
      loading: false,
      error: null,
      data: {
        id: userEmail || "standalone-user",
        email: userEmail || "unknown@localhost",
        name: userName || userEmail || "Standalone user",
      },
    }),
    [userEmail, userName]
  );

  if (typeof children === "function") return children(result);
  return children ?? null;
}

UserQuery.query = async () => {
  const { userEmail, userName } = getRuntimeConfig();
  return {
    data: {
      id: userEmail || "standalone-user",
      email: userEmail || "unknown@localhost",
      name: userName || userEmail || "Standalone user",
    },
  };
};

export function AccountsQuery({ children }) {
  const { accountId } = getRuntimeConfig();
  const result = useMemo(
    () => ({
      loading: false,
      error: null,
      data: accountId ? [{ id: accountId, name: String(accountId) }] : [],
    }),
    [accountId]
  );

  if (typeof children === "function") return children(result);
  return children ?? null;
}

AccountsQuery.query = async () => {
  const { accountId } = getRuntimeConfig();
  return { data: accountId ? [{ id: accountId, name: String(accountId) }] : [] };
};

export const logger = {
  log: (...args) => console.log(...args),
  error: (...args) => console.error(...args),
  warn: (...args) => console.warn(...args),
};
